// Reads the JSON Schema (2020-12, as schemars emits it) from /api/sys/schema.json for the explorer.

/** Follows `$ref` and unwraps `anyOf: [X, null]`; returns { schema, nullable }. */
export function resolve(schema, defs = {}) {
  let current = schema || {}
  let nullable = false
  for (let depth = 0; depth < 10; depth++) {
    if (current.$ref) {
      current = defs[current.$ref.split('/').pop()] || {}
      continue
    }
    const variants = current.anyOf || current.oneOf
    if (variants && variants.some(isNull) && variants.filter((v) => !isNull(v)).length === 1) {
      nullable = true
      current = { ...variants.find((v) => !isNull(v)), description: current.description || variants.find((v) => !isNull(v)).description }
      continue
    }
    break
  }
  if (Array.isArray(current.type) && current.type.includes('null')) {
    nullable = true
    const types = current.type.filter((t) => t !== 'null')
    current = { ...current, type: types.length === 1 ? types[0] : types }
  }
  return { schema: current, nullable }
}

function isNull(variant) {
  return variant?.type === 'null' || (Array.isArray(variant?.type) && variant.type.length === 1 && variant.type[0] === 'null')
}

/** Allowed values for string enums, whether written as `enum` or `oneOf` of `const`s. */
export function enumValues(schema) {
  if (Array.isArray(schema.enum)) return schema.enum.filter((v) => v !== null)
  const variants = schema.oneOf || schema.anyOf
  if (variants && variants.every((v) => 'const' in v || Array.isArray(v.enum))) {
    return variants.flatMap((v) => ('const' in v ? [v.const] : v.enum))
  }
  return null
}

/** Human type label: "string", "integer", "array of string", "one of: a, b", "Theme". */
export function typeLabel(schema, defs = {}) {
  const { schema: s } = resolve(schema, defs)
  const values = enumValues(s)
  if (values) return `one of: ${values.join(', ')}`
  if (s.type === 'array') return `array of ${typeLabel(s.items || {}, defs)}`
  if (Array.isArray(s.type)) return s.type.join(' | ')
  if (s.type) return s.type
  if (schema?.$ref) return schema.$ref.split('/').pop()
  return 'any'
}

/** Constraint notes from validator attributes: "1 to 80 chars", "1 to 3650", "max 50 items". */
export function constraints(schema, defs = {}) {
  const { schema: s } = resolve(schema, defs)
  const notes = []
  if (s.minLength !== undefined || s.maxLength !== undefined) notes.push(range(s.minLength, s.maxLength, ' chars'))
  if (s.minimum !== undefined || s.maximum !== undefined) notes.push(range(s.minimum, s.maximum, ''))
  if (s.minItems !== undefined || s.maxItems !== undefined) notes.push(range(s.minItems, s.maxItems, ' items'))
  if (s.pattern) notes.push(`pattern ${s.pattern}`)
  if (s.format && !['int64', 'uint64', 'int32', 'uint32', 'uint8'].includes(s.format)) notes.push(s.format)
  return notes
}

function range(min, max, unit) {
  if (min !== undefined && max !== undefined) return `${min} to ${max}${unit}`
  if (min !== undefined) return `min ${min}${unit}`
  return `max ${max}${unit}`
}

/** Rows for an object schema: [{ name, type, required, description, notes, schema }]. */
export function fields(schema, defs = {}) {
  const { schema: s } = resolve(schema, defs)
  const required = new Set(s.required || [])
  const rows = Object.entries(s.properties || {}).map(([name, prop]) => {
    const { nullable } = resolve(prop, defs)
    return {
      name,
      type: typeLabel(prop, defs),
      required: required.has(name) && !nullable,
      nullable,
      description: prop.description || resolve(prop, defs).schema.description || '',
      notes: constraints(prop, defs),
      schema: prop,
    }
  })
  // schemars flattens `#[serde(flatten)]` into allOf
  for (const part of s.allOf || []) rows.push(...fields(part, defs))
  return rows
}

/** Placeholder value for examples and the try-it form. */
export function example(schema, defs = {}, name = '') {
  const { schema: s } = resolve(schema, defs)
  const values = enumValues(s)
  if (values) return values[0]
  if (s.type === 'integer' || s.type === 'number') return s.minimum ?? 1
  if (s.type === 'boolean') return true
  if (s.type === 'array') return [example(s.items || {}, defs, name)]
  if (s.type === 'string') {
    if (name === 'org') return 'org_K3j9X2m8P1qa'
    if (name === 'email' || s.format === 'email') return 'ana@example.com'
    return name ? `example ${name.replace(/_/g, ' ')}` : 'example'
  }
  return null
}

/** Example params: required fields only, or every field with `all`. */
export function exampleParams(schema, defs = {}, { all = false } = {}) {
  const params = {}
  for (const field of fields(schema, defs)) {
    if (field.required || all) params[field.name] = example(field.schema, defs, field.name)
  }
  return params
}

/** Params object as repeated form pairs: `a=1&b=x&b=y`. */
export function formPairs(params) {
  const pairs = []
  for (const [key, value] of Object.entries(params)) {
    for (const item of Array.isArray(value) ? value : [value]) pairs.push([key, String(item)])
  }
  return pairs
}
